import type { Dispatch } from '@reduxjs/toolkit';
import type {
  ICustomerState,
  IGetCustomerListQueryParams,
  INewAndUpdateCaseManagerDto,
  INewAndUpdateCustomerDto,
} from 'src/legacy/@types/customer';

import { createSlice } from '@reduxjs/toolkit';
import axios from 'src/legacy/utils/axios';

// ----------------------------------------------------------------------

const initialState: ICustomerState = {
  actionStatus: {
    caseManager: {
      create: null,
      delete: 'none',
      update: null,
    },
    create: 'none',
    delete: 'none',
    update: 'none',
  },
  caseManagers: null,
  customers: null,
  error: null,
  isLoading: false,
  selectedCaseManager: null,
  selectedCustomer: null,
};

const slice = createSlice({
  initialState,
  name: 'customer',
  reducers: {
    // CLEAR ERROR
    clearError(state) {
      state.error = '';
    },

    createCaseManagerFailed(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        caseManager: {
          ...state.actionStatus.caseManager,
          create: [
            ...(state.actionStatus.caseManager.create || []),
            action.payload,
          ],
        },
      };
    },

    // CREATE NEW CASE MANAGER
    createCaseManagerStart(state) {
      state.actionStatus = {
        ...state.actionStatus,
        caseManager: {
          ...state.actionStatus.caseManager,
          create: null,
        },
      };
    },

    createCaseManagerSuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        caseManager: {
          ...state.actionStatus.caseManager,
          create: null,
        },
      };
      state.error = null;
      state.caseManagers = [...(state.caseManagers || []), action.payload];
    },

    // CREATE NEW CUSTOMER
    createCustomerSuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        create: 'complete',
      };
      state.error = null;
      state.customers = [...(state.customers || []), action.payload];
    },

    deleteCaseManagerFailed(state) {
      state.actionStatus = {
        ...state.actionStatus,
        caseManager: {
          ...state.actionStatus.caseManager,
          delete: 'failed',
        },
      };
    },

    // DELETE CASE MANAGER
    deleteCaseManagerStart(state) {
      state.actionStatus = {
        ...state.actionStatus,
        caseManager: {
          ...state.actionStatus.caseManager,
          delete: 'loading',
        },
      };
    },

    deleteCaseManagerSuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        caseManager: {
          ...state.actionStatus.caseManager,
          delete: 'complete',
        },
      };
      if (state.caseManagers) {
        state.caseManagers = state.caseManagers.filter(
          (leader) => leader.id !== action.payload.leaderId
        );
      }
    },

    // DELETE CUSTOMER
    deleteCustomerSuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        delete: 'complete',
      };
      if (state.customers) {
        state.customers = state.customers.filter(
          (customer) => customer.id !== action.payload.customerId
        );
      }
    },

    // GET CASE MANAGER LIST
    getCaseManagerListSuccess(state, action) {
      state.isLoading = false;
      state.caseManagers = action.payload;
    },

    // GET CASE MANAGER
    getCaseManagerSuccess(state, action) {
      state.isLoading = false;
      state.selectedCaseManager = action.payload;
    },

    // GET CUSTOMERS
    getCustomerListSuccess(state, action) {
      state.isLoading = false;
      state.customers = action.payload;
    },

    // GET CUSTOMER
    getCustomerSuccess(state, action) {
      state.isLoading = false;
      state.selectedCustomer = action.payload;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // UPDATE ACTION STATUS
    updateActionState(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        ...action.payload,
      };
    },

    updateCaseManagerFailed(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        caseManager: {
          ...state.actionStatus.caseManager,
          update: [
            ...(state.actionStatus.caseManager.update || []),
            action.payload,
          ],
        },
      };
    },

    // UPDATE CASE MANAGER
    updateCaseManagerStart(state) {
      state.actionStatus = {
        ...state.actionStatus,
        caseManager: {
          ...state.actionStatus.caseManager,
          update: null,
        },
      };
    },

    updateCaseManagerSuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        caseManager: {
          ...state.actionStatus.caseManager,
          update: null,
        },
      };
      state.error = null;
      if (state.caseManagers) {
        state.caseManagers = state.caseManagers.map((leader) => {
          if (leader.id === action.payload.id) {
            return action.payload;
          }
          return leader;
        });
      }
    },

    // UPDATE CUSTOMER
    updateCustomerSuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        update: 'complete',
      };
      state.error = null;
      if (state.customers) {
        state.customers = state.customers.map((customer) => {
          if (customer.id === action.payload.id) {
            return action.payload;
          }
          return customer;
        });
      }
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  createCaseManagerSuccess,
  createCustomerSuccess,
  getCaseManagerListSuccess,
  getCustomerSuccess,
  updateActionState,
} = slice.actions;

// ----------------------------------------------------------------------
// --------------              Customer Actions            --------------
// ----------------------------------------------------------------------

export const getCustomerList = (
  companyId: string,
  params: IGetCustomerListQueryParams
) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const response = await axios.get(
        `/organization/companies/${companyId}/customers/`
      );
      let allResults: any[] = response.data.results;

      if (
        response.data.count > allResults.length ||
        response.data.next !== ''
      ) {
        await Promise.all(
          Array(Math.ceil(response.data.count / 20) - 1)
            .fill(0)
            .map(async (_, index) => {
              const response2 = await axios.get(
                `/organization/companies/${companyId}/customers/`,
                {
                  params: { ...params, page: index + 2 },
                }
              );
              allResults = [...allResults, ...response2.data.results];
            })
        );
      }

      dispatch(slice.actions.getCustomerListSuccess(allResults));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const createCustomer = (
  companyId: string,
  newCustomer: INewAndUpdateCustomerDto
) => {
  return async (dispatch: Dispatch<any>) => {
    dispatch(
      slice.actions.updateActionState({ create: 'loading', error: null })
    );
    try {
      const response = await axios.post(
        `/organization/companies/${companyId}/customers/`,
        newCustomer
      );
      if (response.data.id && newCustomer.case_managers.length > 0) {
        newCustomer.case_managers.forEach((manager) => {
          dispatch(createCaseManager(companyId, response.data.id, manager));
        });
      }
      dispatch(slice.actions.createCustomerSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.updateActionState({ create: 'failed' }));
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const getCustomer = (companyId: string, customerId: number) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(
        `/organization/companies/${companyId}/customers/${customerId}/`
      );
      dispatch(slice.actions.getCustomerSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const updateCustomer = (
  companyId: string,
  customerId: number,
  updatedCustomer: INewAndUpdateCustomerDto
) => {
  return async (dispatch: Dispatch<any>) => {
    dispatch(
      slice.actions.updateActionState({ error: null, update: 'loading' })
    );
    try {
      if (updatedCustomer.case_managers.length > 0) {
        updatedCustomer.case_managers.forEach((manager) => {
          if (manager.customer) {
            if (manager.isDeleted) {
              dispatch(deleteCaseManager(companyId, customerId, manager.id));
            }
            if (manager.isModified) {
              dispatch(
                updateCaseManager(companyId, customerId, manager.id, manager)
              );
            }
          } else {
            dispatch(createCaseManager(companyId, customerId, manager));
          }
        });
      }
      const response = await axios.put(
        `/organization/companies/${companyId}/customers/${customerId}/`,
        updatedCustomer
      );
      dispatch(slice.actions.updateCustomerSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.updateActionState({ update: 'failed' }));
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const deleteCustomer = (companyId: string, customerId: number) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.updateActionState({ delete: 'loading' }));
    try {
      await axios.delete(
        `/organization/companies/${companyId}/customers/${customerId}/`
      );
      dispatch(slice.actions.deleteCustomerSuccess({ customerId }));
    } catch (error) {
      dispatch(slice.actions.updateActionState({ delete: 'failed' }));
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------
// --------------            Case Manager Actions          --------------
// ----------------------------------------------------------------------

export const getCaseManagerList = (companyId: string, customerId: number) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(
        `/organization/companies/${companyId}/customers/${customerId}/case-managers/`
      );
      dispatch(slice.actions.getCaseManagerListSuccess(response.data.results));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const createCaseManager = (
  companyId: string,
  customerId: number,
  newCaseManager: INewAndUpdateCaseManagerDto
) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.createCaseManagerStart());
    try {
      const response = await axios.post(
        `/organization/companies/${companyId}/customers/${customerId}/case-managers/`,
        newCaseManager
      );
      dispatch(slice.actions.createCaseManagerSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.createCaseManagerFailed(error));
    }
  };
};

// ----------------------------------------------------------------------

export const getCaseManager = (
  companyId: string,
  customerId: number,
  caseManagerId: number
) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(
        `/organization/companies/${companyId}/customers/${customerId}/case-managers/${caseManagerId}/`
      );
      dispatch(slice.actions.getCaseManagerSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const updateCaseManager = (
  companyId: string,
  customerId: number,
  caseManagerId: number,
  updatedCaseManager: INewAndUpdateCaseManagerDto
) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.updateCaseManagerStart());
    try {
      const response = await axios.put(
        `/organization/companies/${companyId}/customers/${customerId}/case-managers/${caseManagerId}/`,
        updatedCaseManager
      );
      dispatch(slice.actions.updateCaseManagerSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.updateCaseManagerFailed(error));
    }
  };
};

// ----------------------------------------------------------------------

export const deleteCaseManager = (
  companyId: string,
  customerId: number,
  caseManagerId: number
) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.deleteCaseManagerStart());
    try {
      await axios.delete(
        `/organization/companies/${companyId}/customers/${customerId}/case-managers/${caseManagerId}/`
      );
      dispatch(slice.actions.deleteCaseManagerSuccess({ caseManagerId }));
    } catch (error) {
      dispatch(slice.actions.deleteCaseManagerFailed());
      dispatch(slice.actions.hasError(error));
    }
  };
};
